export default {
  overview: {
    title: "Customer Overview",
    archive_title: "Archived customers",
    subtitle: "List of all customers.",
    actions: {
      create_new: "Create new customer",
    },
    search: {
      placeholder: "Search",
    },
    table: {
      headers: {
        id: "ID",
        company: "Company",
        note: "Notes",
        contact: "Contact person",
        projects: "Projects",
        createdAt: "Created at",
      },
      more_contacts: "+{count} more",
      projects: {
        total: "{n} project | {n} projects",
        none: "No projects",
      },
      empty: {
        note: "Not set",
      },
    },
  },
  detail: {
    title: "Customer Details",
    actions: {
      update: "Save",
      actions: "Actions",
      search_material: "Search materials",
      search_machine: "Search machines",
      search_additional_effort: "Search additional efforts",
    },
    tabs: {
      general: "General",
      additional_efforts: "Additional Efforts",
      machines: "Machines",
      materials: "Materials",
      contacts: "Contacts",
      files: "File storage",
      userPositions: "Employee Positions",
      configuration: "Configuration",
      activity: "Activity",
    },
    form: {
      company: "Company",
      note: "Notes",
      // The address fields themselves live under `general.address.*`.
      address_search_unavailable_hint: "Enter the address in the fields below.",
      default_currency: "Currency",
      configuration: {
        section: "Currency",
        field: "Default currency",
        currency_hint: "Applies to all of this customer's prices.",
      },
      sections: {
        base_data: "Master data",
        address: "Address",
        note: "Note",
      },
      materials: {
        text: "Materials",
        add_material: "Add a Material...",
        add_material_no_data: "Enter the name of a material and click on it to add it.",
        radio: {
          fixed_price: "Fixed Price",
          variable_price: "Variable Price",
        },
        selling_price: "Selling Price",
        not_set: "not set",
        surcharge: "Surcharge",
        purchase_price: "Purchase Price",
        delete: "Delete",
        multiplier: "Multiplier",
        assign: "Assign material",
        empty: "No materials assigned yet.",
        filter: "Filter materials",
        empty_hint: "Leave empty to set the price in the project",
        remove: "Remove material",
        restore: "Undo deletion",
        packet_badge: "Packet",
        material_count: "{count} materials",
        // The material's own purchase prices: a customer has none of its own, they are kept on the material.
        purchase_prices: {
          record_default: "the material's default",
          dangling: "The preferred price no longer exists on the material. Projects start with its default.",
          // Above the list behind "Change": what this customer's projects start with.
          menu_title: "For this customer's projects",
          // Under it, with suppliers: the prices here are the suppliers' before their discount; a project books them
          // less the discount.
          list_prices: "List prices, before supplier discounts",
          // Without suppliers (before the setup) the line under the selling price: "Purchase price: Night · Change".
          price: "Purchase price",
          all: "All prices",
          missing_in_currency: "No price in {currency}",
          count: "{count} price | {count} prices",
          set: "Add",
          set_title: "Add the purchase price on the material",
        },
        status: {
          created: "New",
          updated: "Changed",
          deleted: "Removed",
        },
        assign_dialog: {
          title: "Assign materials",
          subtitle: "Pick one or more materials from the catalogue. Set prices afterwards per row.",
          search_label: "Search material",
          no_results: "No material found",
          add: "Add",
          cancel: "Cancel",
        },
      },
      machines: {
        text: "Machines",
        add_machine: "Add a Machine...",
        add_machine_no_data: "Enter the name of a machine and click on it to add it.",
        radio: {
          fixed_price: "Fixed Price",
          variable_price: "Variable Price",
        },
        selling_price: "Selling Price",
        not_set: "not set",
        surcharge: "Surcharge",
        purchase_price: "Purchase Price",
        delete: "Delete",
        multiplier: "Multiplier",
        assign: "Assign machine",
        empty: "No machines assigned yet.",
        filter: "Filter machines",
        selling_preview: "results in selling price",
        empty_hint: "Leave empty to set the price in the project",
        remove: "Remove machine",
        restore: "Undo deletion",
        packet_badge: "Packet",
        machine_count: "{count} machines",
        // The machine's own purchase prices: a customer has none of its own, they are kept on the machine.
        purchase_prices: {
          record_default: "the machine's default",
          dangling: "The preferred price no longer exists on the machine. Projects start with its default.",
          // Above the list behind "Change": what this customer's projects start with.
          menu_title: "For this customer's projects",
          // Under it, with suppliers: the prices here are the suppliers' before their discount; a project books them
          // less the discount.
          list_prices: "List prices, before supplier discounts",
          // Without suppliers (before the setup) the line under the selling price: "Purchase price: Night · Change".
          price: "Purchase price",
          all: "All prices",
          missing_in_currency: "No price in {currency}",
          count: "{count} price | {count} prices",
          set: "Add",
          set_title: "Add the purchase price on the machine",
        },
        status: {
          created: "New",
          updated: "Changed",
          deleted: "Removed",
        },
        assign_dialog: {
          title: "Assign machines",
          subtitle: "Pick one or more machines from the catalogue. Set prices afterwards per row.",
          search_label: "Search machine",
          no_results: "No machine found",
          packet_info:
            "This packet adds {count} machines. They are added without a price and can be priced individually afterwards.",
          add: "Add",
          cancel: "Cancel",
        },
      },
      additional_efforts: {
        text: "Additional Efforts",
        add_additional_effort: "Add an Additional Effort...",
        add_additional_effort_no_data: "Enter the name of an additional effort and click on it to add it.",
        radio: {
          fixed_price: "Fixed Price",
          variable_price: "Variable Price",
        },
        selling_price: "Selling Price",
        not_set: "not set",
        surcharge: "Surcharge",
        purchase_price: "Purchase Price",
        delete: "Delete",
        multiplier: "Multiplier",
        assign: "Assign effort",
        empty: "No efforts assigned yet.",
        filter: "Filter efforts",
        empty_hint: "Leave empty to set the price in the project",
        remove: "Remove effort",
        restore: "Undo deletion",
        packet_badge: "Packet",
        effort_count: "{count} efforts",
        status: {
          created: "New",
          updated: "Changed",
          deleted: "Removed",
        },
        assign_dialog: {
          title: "Assign efforts",
          subtitle: "Pick one or more efforts from the catalogue. Set prices afterwards per row.",
          search_label: "Search effort",
          no_results: "No effort found",
          add: "Add",
          cancel: "Cancel",
        },
      },
      contacts: {
        text: "Contacts",
        add_contact: "Add a new Contact",
        add: "Add",
        add_title: "Add a new Contact",
        add_subtitle: "Contact of the customer",
        edit_title: "Edit Contact",
        edit_subtitle: "Contact of the customer",
        all_contacts: "Contacts",
        empty: "No contacts added yet.",
        table: {
          contact: "Contact",
          phone: "Phone",
        },
        delete: {
          title: "Delete contact",
          text: "Do you really want to delete this contact?",
          agree: "Delete",
          disagree: "Cancel",
        },
        sections: {
          person: "Person",
          contact: "Contact",
        },
        position: "Position",
        email: "E-Mail",
        lastName: "Lastname",
        firstName: "Firstname",
        telephoneNumber: "Telephone Number",
        mobileTelephoneNumber: "Mobile Telephone Number",
        gender: {
          salutation: "Salutation",
          mr: "Mister",
          ms: "Miss",
          generic: "Rather not say",
        },
      },
      userposition: {
        text: "Employee Positions",
        add_userposition: "Add Employee Position",
        noData: "No Employee Position available",
        delete: "Delte",
        hourlyRate: "Hourly Rate",
        assign: "Assign position",
        filter: "Filter positions",
        empty: "No employee positions assigned yet.",
        not_set: "Not set",
        remove: "Remove position",
        assign_dialog: {
          title: "Assign positions",
          subtitle: "Pick one or more positions. Adjust the hourly rate afterwards per row.",
          search_label: "Search position",
          no_results: "No position found",
          add: "Add",
          cancel: "Cancel",
        },
      },
    },
  },
  create: {
    title: "Create Customer",
    subtitle: "Create a new Customer.",
  },
  contact: {
    position: {
      no_results: 'No results matching "<strong>{value}</strong>"',
      manage: "Manage",
      overview: {
        title: "All contact positions",
        subtitle: "List of all customer contact positions.",
        empty_items: "No positions available",
        add_item: "Add new position",
      },
    },
  },
}
